import { type QuickReply } from '@/karehero-app/tell-us-once/intake/QuickReplies';
import { type FactGroup } from '@/karehero-app/tell-us-once/intake/FactList';
import {
  FORK_OPTIONS,
  FORK_QUESTION,
} from '@/karehero-app/onboarding/Onboarding.content';

export const RECIPIENT = 'Margaret';

/**
 * The fork's answers, reordered: the two that say who needs care sit together,
 * and just exploring goes last. A local list rather than a change to
 * Onboarding.content, which the master prototype reads.
 */
export const INTAKE_FORK_OPTIONS = [
  FORK_OPTIONS[0],
  FORK_OPTIONS[2],
  FORK_OPTIONS[1],
];

const ABOUT_THEM = "Tell us about the person you're caring for";
const ABOUT_YOU = 'Tell us about you';

/**
 * A question in the intake chat.
 *
 * `pick` is closed: the answers sit above Next, so there is no input and nothing
 * is listening. `fields` is closed too, and asks for more than one thing at a
 * time. `say` is open: talk or type.
 *
 * `rows` is closed as well, but the answers are rows in the conversation and
 * there is no bar at all: tapping one answers, so there is nothing for a Next to
 * confirm. It suits a question with two or three short answers, which is most of
 * the closed questions here, and it keeps the answering in the transcript where
 * everything else the person does happens.
 */
export type ChatQuestion = {
  id: string;
  ask: string;
  mode: 'pick' | 'say' | 'fields' | 'rows';
  options?: string[];
  fields?: { label: string; value?: string }[];
  /**
   * Rows in the transcript under the question: things to do instead of, or as
   * well as, answering it. `go` is the one that leaves the question.
   */
  asides?: {
    /** Left off where the row is an answer rather than something to do. */
    icon?: string;
    label: string;
    tone?: 'offer' | 'go';
    /**
     * An answer drawn grey like the quieter rows, for the one we would rather
     * they did not pick, like turning down the AI. Still an answer, in place.
     */
    quiet?: boolean;
    /** Held back until they have answered this question. */
    after?: 'answer';
    /**
     * Tapping this hands something over, and this is what lands in the
     * transcript. Without it the row is an offer that answers in words.
     */
    uploads?: 'photo' | 'document';
    /**
     * What the file is called in the transcript. Every document used to be the
     * discharge summary, which is the right thing to hand over when the
     * question is about a care situation and nonsense when it is asking for the
     * name on your official documents.
     */
    file?: string;
    /**
     * Tapping this opens the composer rather than answering.
     *
     * A question carrying one of these starts with no bar at all, so the offer
     * above it is a real choice: hand a document over, or type it. With the
     * composer already open, typing was the default and the upload row was a
     * thing you could also do, which is not what the question is asking.
     */
    opensInput?: boolean;
  }[];
  /** Off hides the orb: a question that has to be typed rather than spoken. */
  voice?: boolean;
  /** What the composer's field says while it is empty. */
  placeholder?: string;
  /**
   * Said back when a typed answer will not do, and the question stays open.
   * Returns nothing when the answer is fine. Rows are never checked.
   */
  check?: (value: string) => string | undefined;
  /**
   * Said before the ask, a bubble each.
   *
   * For a question that has to show its working first: reopening the
   * conversation to correct something means saying what we already hold before
   * asking what is wrong with it, or they are being asked to remember what they
   * told us and spot the difference.
   */
  says?: string[];
  /**
   * A second thing said straight after the ask, in its own bubble.
   *
   * For something about how to answer rather than what to answer, so it stays
   * out of the question itself. Set it where a control needs introducing once,
   * not on every question that has one.
   */
  hint?: string;
  /** Harness-only: the answer to show when a later question is opened directly. */
  answer?: string;
  /**
   * Harness-only: that answer was typed, so it shows as text rather than a
   * voice note. For a free answer whose words matter, like "82" being read as
   * 66 or over.
   */
  typed?: boolean;
  /** Stays open for as many answers as they want, and moves on with Next. */
  multi?: boolean;
  /**
   * With attachRows: more than one of the answers can be picked. Each tap
   * ticks one, and Done sends the lot. A quiet answer, like None of these,
   * stands alone, so it still answers straight away.
   */
  many?: boolean;
  /** Harness-only: with many, the answers already ticked. */
  ticked?: string[];
  /**
   * Each thing they send is read as it arrives, with a spinner beside it until
   * it is. A checklist card only ticks what has been read.
   */
  readsEach?: boolean;
  /**
   * The rows are drawn as buttons attached under the ask, WhatsApp-style,
   * rather than as rows of their own. The title sits above the ask in the
   * same bubble.
   */
  attachRows?: { title?: string; more?: string[] };
  /**
   * Opens as a new conversation: the history behind it is cleared.
   *
   * A "Continue when you're done" always lands on one of these. Taking the way
   * on closes a subject, so carrying the transcript through it leaves the thing
   * they just finished sitting above the next question as though it were still
   * live. Answering a question is not the same thing: that flows on, and the
   * history stays.
   */
  restart?: boolean;
  /** Whose questions these are, shown small at the top of the screen. */
  section?: string;
  /**
   * Likely answers as chips just above the text field, side by side. Tapping
   * one sends it; the field stays for anything else. See QuickReplies.
   */
  quickReplies?: QuickReply[];
  /**
   * Once answered, what is said while the answer is read: Keni's bubble cycles
   * these lines, the composer waits, and the next turn arrives after `ms`. In
   * place of the usual short typing beat.
   */
  reading?: { lines: string[]; ms: number };
  /**
   * Something given back before the question is asked: a card from us, not a
   * Keni bubble. Onboarding 1.1's give-backs (the warming-up doc, ideas 1B and
   * 1C). Attached to an existing step, never a step of its own, because saved
   * progress is by step index.
   */
  card?: FromUs;
  /**
   * Claims, drawn as the benefits calculator's result rows (ClaimRow) under the
   * ask: one coral for the one to do next, the rest lime. For a turn that hands
   * back what they could get, in the rows the app uses for it everywhere else.
   */
  claims?: {
    icon: string;
    name: string;
    description: string;
    lead?: boolean;
  }[];
  /**
   * What we hold, as answer rows (FactList) before the ask: answered rows, gaps
   * as Not answered, each row editing in a drawer. For a turn that opens with
   * what we already have, so the conversation is for what is missing.
   */
  facts?: FactGroup[];
  /** A second card after the first, before the ask. */
  thenCard?: FromUs;
  /**
   * Quieter rows (`attachRows.more`) that open a sheet explaining something
   * instead of answering: each one's label, the sheet's title and its points.
   */
  explain?: {
    label: string;
    title: string;
    points: string[];
    /** Stays under the message once it is answered, and still opens. */
    keep?: boolean;
    /**
     * A link under the points, to where the whole of it lives: "Read the full
     * AI policy". No `href` while the page it goes to does not exist yet.
     */
    link?: { lead: string; label: string; href?: string };
  }[];
  /** The answer that leaves the chat altogether, like turning down the AI. */
  leaves?: string;
  /**
   * Where an answer goes when it is not the next question in line: an
   * answer's label to a question's id, with '*' for any answer. 'end' finishes
   * the conversation.
   *
   * Branches sit after the main path in the list, so the main path's last
   * question ends with `{ '*': 'end' }` to stop short of them. A scene opened
   * on a branch walks the same way, by each question's `answer`.
   */
  next?: Record<string, string>;
};

/** A part of a claim, as ClaimParts shows it. */
export type ClaimPart = {
  title: string;
  /** Its chip. */
  short: string;
  /** What we will ask, in a line, for the preview. */
  line: string;
  /** What it needs, one thing each. */
  needs: string[];
  /** What has come in for each need, in the same order; blank for not yet. */
  found?: string[];
};

/** A card from us in the transcript. See FromUsCard. */
export type FromUs = {
  label: string;
  body?: string;
  /**
   * A button at the foot of the card, the way the Home card has one: for a
   * card that is the way on, like the calculator's results.
   */
  action?: string;
  /**
   * The coral sparkle before the label. Off where the card is ours, not the
   * AI's.
   */
  sparkle?: boolean;
  /** A short list, one line each: prompts, or what is being put together. */
  items?: string[];
  /** Under the question rather than before it, for prompts that answer it. */
  after?: boolean;
  /** The label reads as a line in sentence case, not as an overline tag. */
  sentence?: boolean;
  /** Its lines can be corrected, from a pencil in the card's corner. */
  editable?: boolean;
  /**
   * What the lines are made from, as answer rows. With `editable`, the pencil
   * opens these in a drawer (FactsSheet), each editing in a drawer of its own,
   * rather than turning the lines into text.
   */
  facts?: FactGroup[];
  /**
   * A checklist, ticked off as they send things after the question with this
   * id is asked, and held at the top of the chat until that question is
   * answered.
   */
  checklistUntil?: string;
  /**
   * Ticked by reading the answer rather than by counting what is sent. While
   * the answer to `checklistUntil` is read the items tick one after another,
   * as the reading finds each in what they said, and all of them are ticked
   * once it has been.
   */
  checklistReads?: boolean;
  /**
   * What the reading found for each item, in the same order. Shown small under
   * an item once it is ticked, so they see what we took as it is taken.
   */
  found?: string[];
  /**
   * A checklist as it stands, with this many ticked, for a card that is a
   * snapshot rather than live (see FromUsCard's `checked`). Live checklists
   * use `checklistUntil` instead.
   */
  checked?: number;
  /**
   * A claim in its parts, in place of items: a preview of each, and what one
   * still needs once it is started, with chips between. See ClaimParts.
   */
  claim?: ClaimPart[];
  /** Which view of the claim opens: 'all', or a part by its index. */
  claimView?: 'all' | number;
  note?: string;
  /** The care experts, by first name, for the card that introduces them. */
  people?: string[];
};

export const SITUATION: ChatQuestion = {
  id: 'situation',
  section: ABOUT_THEM,
  ask: 'Tell us what looking after them involves. Talk or type, whichever is easier, and there is no right way to answer this.',
  mode: 'say',
};

/** Option 1: the opening questions were screens, so the chat only asks this. */
export const INTAKE_CHAT: ChatQuestion[] = [SITUATION];

/**
 * Option 2: the same two opening questions, in the chat. Copy is read from the
 * onboarding content rather than retyped, so the two options are comparable.
 */
const SITUATION_V2: ChatQuestion = {
  id: 'situation',
  section: ABOUT_THEM,
  // Not by name. The name may have come off a photo of an ID that is still
  // being read, and a question that uses it either waits for the read or says
  // something we do not know yet.
  ask: 'Tell us about them, any conditions they have, and what looking after them involves. Talk or type, whichever is easier.',
  // The first time the orb appears. It is a lovely thing that says nothing
  // about itself, and the question asks them to talk without saying what to
  // tap. Said once, here: by the next spoken question they know.
  // The plus, said here because this is the first question with the composer
  // and the plus both on screen, and where a document helps most.
  hint: "Tap the orb to record, then press it again when you're done. Upload any supporting documents during this chat using the +.",
  mode: 'say',
};

// The follow-up, once something has landed. Lighter than the first ask, and it
// stays open: the point is that more can be added, not that a box is filled.
//
// It said only "Tell us more if you want to", which is an invitation with
// nothing to take hold of. The three things worth having are the conditions,
// how the care is arranged, and what they want from us, so the question names
// them. "Could" rather than "tell us", because it is still optional and the
// way on is right underneath it.
const ANYTHING_ELSE: ChatQuestion = {
  id: 'anything-else',
  section: ABOUT_THEM,
  // Same reason, and it drops the "her" with it: nothing so far has told us
  // that either.
  ask: 'You could tell us about any conditions they have, how their care is arranged, or what you need help with.',
  mode: 'say',
  // Said rather than offered. As a row it was a button competing with the way
  // on directly under it, and the plus in the composer already does the job:
  // this points at the plus instead of duplicating it.
  hint: 'If you have any documents or evidence that might help, add them with the plus.',
  multi: true,
  asides: [
    { icon: 'chevron-right', label: "Continue when you're done", tone: 'go' },
  ],
};

// What closes the capture now that the follow-up is gone. The composer stays
// open, so adding more is just carrying on, and the way on is the one row. New
// messages land above it, so it stays the last thing in the thread.
//
// It also keeps the chat from ending under them. The capture is the last thing
// the intake asks, so without a question after it the first thing sent ran
// straight into the reading with nothing said about it.
const ANYTHING_MORE: ChatQuestion = {
  id: 'anything-more',
  section: ABOUT_THEM,
  ask: 'Is that it, or is there anything else you want to tell us?',
  // `say` rather than `rows`: a rows question has no bar at any point, and the
  // bar has to stay after the first message.
  mode: 'say',
  asides: [{ icon: 'chevron-right', label: "I'm done", tone: 'go' }],
};

// The carer's own name, and the first thing the conversation asks. Their record,
// not the recipient's.
//
// It used to sit at the end, after everything about the person they care for.
// Asked after the fork: by the time we ask for a name we already know who they
// are here for, so it reads as part of a conversation about somebody rather
// than as an account being opened.
//
// Legal name, because it is the name every claim has to be filed under. What
// they would rather be called is the next question, and that is the one the app
// uses after this.
const CARER_NAME: ChatQuestion = {
  id: 'carer-name',
  section: ABOUT_YOU,
  // As on dev: the name they signed up with, read back for a one-tap yes. The
  // field stays open for typing a different one. Written out rather than read
  // off `answer`, which is declared below.
  ask: 'Before we get going, can you confirm your name is Vez Maxwell, or type your name below?',
  mode: 'say',
  voice: false,
  answer: 'Vez Maxwell',
  placeholder: 'Or type your name…',
  asides: [{ label: "Yes, that's me" }],
};

/**
 * The first name out of the legal name they just gave, so the offer can say it
 * back to them.
 *
 * Read off `CARER_NAME.answer` rather than written out again: that is the answer
 * the harness fills the question above with, so the two names cannot drift apart
 * in the prototype. In the app it is whatever they typed.
 */
const CARER_FIRST_NAME = (CARER_NAME.answer ?? '').split(' ')[0];

/**
 * What the app bar's avatar carries once the chat has their name. Read off the
 * same answer as the first name above, so the bar cannot end up showing a
 * different person from the one in the transcript.
 */
/** What the app bar says while the intake is asking. */
export const INTAKE_BAR = 'Tell us about you';

/** And while they are reading back what it heard. */
export const REVIEW_BAR = 'Review what you told us';

export const CARER_INITIALS = (CARER_NAME.answer ?? '')
  .split(' ')
  .map((part) => part[0] ?? '')
  .join('');

// Optional, and the likely answer is one tap: the first name they already gave.
// A row rather than a pick list, because typing something else is still the main
// way to answer, and an aside with no tone answers the question in its own
// words.
//
// The point of asking at all is that the legal name is on the record and is not
// necessarily what we should call them.
const CARER_PREFERRED_NAME: ChatQuestion = {
  id: 'carer-preferred-name',
  section: ABOUT_YOU,
  ask: "Is there anything you'd prefer to be called?",
  mode: 'say',
  voice: false,
  // The likely answer, and a decline in the same tap: "No" on its own is the
  // thing that used to get typed here, and a name is the one thing this
  // question must not come away with when the answer was no.
  asides: [{ icon: 'check', label: `No, ${CARER_FIRST_NAME} is fine` }],
  // The same words the row above puts in the transcript, so a screen opened
  // after this question shows what tapping it actually said. Without it the
  // harness fell back to a bare 'Yes', and the two screens stopped being the
  // same conversation.
  answer: `No, ${CARER_FIRST_NAME} is fine`,
};

// Their mobile, with the other questions about them. Typed: a number is easier
// to get right typed than said. Placeholder wording.
const CARER_MOBILE: ChatQuestion = {
  id: 'carer-mobile',
  section: ABOUT_YOU,
  ask: "What's your mobile number?",
  mode: 'say',
  voice: false,
  placeholder: 'Type your mobile number…',
  answer: '07700 900461',
  // Optional: a way past it that says so, rather than an empty Next.
  asides: [{ label: "I'd rather not" }],
  check: (value) => checkMobile(value),
};

// The fork first, then the two name questions, then who they are caring for.
//
// Asking who they are supporting before anything else is what makes the rest of
// the conversation about somebody: the name questions that follow read as 'and
// who are you, then' rather than as an account being opened. It also puts the
// one question that changes what comes next at the point where nothing has been
// said yet, so nothing already said has to be reinterpreted.
export const INTAKE_CHAT_V2: ChatQuestion[] = [
  {
    id: 'fork',
    // About you: whether they care for someone is a question about them, and it
    // keeps the bar the same through to the name questions after it.
    section: ABOUT_YOU,
    ask: FORK_QUESTION,
    // Answered in the conversation. It was a radio list over a Next in a bar of
    // its own, which is a form's way of asking three short questions, and it
    // made the one closed question in the intake the only screen where the
    // answering happened somewhere other than the transcript.
    mode: 'rows',
    // Kept alongside the rows: this is what a screen opened after the fork
    // shows as the answer given, and what a caller pre-answering it reads.
    options: INTAKE_FORK_OPTIONS.map((o) => o.label),
    asides: INTAKE_FORK_OPTIONS.map((o) => ({ label: o.label })),
  },
  CARER_NAME,
  CARER_PREFERRED_NAME,
  CARER_MOBILE,
  {
    id: 'who',
    section: ABOUT_THEM,
    ask: "What is the full name of the person you're caring for?",
    mode: 'say',
    voice: false,
    answer: RECIPIENT,
  },
  SITUATION_V2,
  ANYTHING_MORE,
];

/**
 * The intake as it stood before the capture was cut to one message: the
 * situation question, then a follow-up that stayed open for as many answers as
 * they wanted and was left with "Continue when you're done".
 *
 * Kept whole rather than deleted so the two shapes can be read against each
 * other. Nothing in the live flow reads this list; only the frame that opens
 * the follow-up does.
 */
export const INTAKE_CHAT_V2_WITH_FOLLOW_UP: ChatQuestion[] = [
  ...INTAKE_CHAT_V2.filter((q) => q.id !== 'anything-more'),
  ANYTHING_ELSE,
];

/**
 * Where a question sits in the chat, by id.
 *
 * Callers open the chat partway through, and they used to do it by counting:
 * `startAt={3}`. Moving the two name questions to the front made every one of
 * those numbers wrong at once, silently, because a number is always a valid
 * index. Naming the question means the next reorder cannot do that.
 */
export const intakeStepIn = (questions: ChatQuestion[], id: string): number => {
  const i = questions.findIndex((q) => q.id === id);
  if (i < 0) throw new Error(`No intake question called ${id}`);
  return i;
};

export const intakeStep = (id: string): number =>
  intakeStepIn(INTAKE_CHAT_V2, id);

/** The fork's options, for a caller that wants to pre-answer it. */
export const INTAKE_FORK_ANSWERS =
  INTAKE_CHAT_V2[intakeStep('fork')].options ?? [];

/**
 * The same chat down the other fork: they are the person who needs care, so the
 * name we ask for is their own and the section is about them.
 */
export const INTAKE_CHAT_V2_SELF: ChatQuestion[] = INTAKE_CHAT_V2.map((q) => {
  if (q.id === 'fork') return { ...q, answer: INTAKE_FORK_OPTIONS[1].label };
  // OPEN: down this fork the person is the one who needs care, so their name is
  // already answered two questions above and this asks for it again. It is left
  // in because the flows pages draw it as the other side of 'Their full name',
  // and dropping it is a decision about the self branch rather than about where
  // the name question goes.
  if (q.id === 'who')
    return {
      ...q,
      section: ABOUT_YOU,
      ask: "What's your full name?",
      // Inherited from the question it copies, where the ID belongs to someone
      // else.
      asides: [
        {
          icon: 'camera',
          label: 'Upload a photo of your ID',
          uploads: 'photo',
        },
      ],
    };
  return q;
});

/**
 * The AI processing consent, between "You're in" and the first question. Both
 * options reach it, because both send free text to a model.
 *
 * Says why rather than what: the reason a model reads this is that a care
 * situation does not survive being turned into checkboxes. The conditions
 * themselves are a document, and this links to it rather than reciting it.
 */
/**
 * The same consent as AI_CONSENT, carried by Continue on the combined arrival
 * screen instead of taking a screen of its own. The lead and the link come from
 * AI_CONSENT so the two versions can never say different things about the
 * policy, and only the sentence the press commits to lives here.
 */
export const AI_CONSENT_ON_CONTINUE = {
  line: 'By continuing, you agree to let KareHero process your data',
};

export const AI_CONSENT = {
  title: 'Before we start',
  body: 'KareHero uses AI to process data. This makes it easier for us to capture all the nuances of care situations and help you as best possible.',
  policyLead: 'Read the full',
  policyLink: 'AI policy',
  accept: "I'm happy with that",
  decline: 'No thanks',
};

/**
 * What an uploaded file is called in the transcript. A photo needs no name, it
 * shows itself; a document cannot, so it says what it is.
 */
/**
 * What the plus in the composer offers. A camera icon there only ever meant one
 * of the two things they can hand over, and the flow accepts both, so the icon
 * stopped being a claim about what it does and became a plus that asks.
 */
/** The desktop bar. On a phone the app's chrome carries this; here it does not. */
export const INTAKE_ATTACH = {
  title: 'Upload',
  photos: 'Photos',
  files: 'Files',
};

export const INTAKE_UPLOADS = {
  // A discharge summary rather than an Attendance Allowance letter: nothing has
  // mentioned a claim at this point in the conversation, and this is the kind of
  // thing a carer actually has to hand. It is also a source the review already
  // names, so the two agree about where facts come from.
  document: 'Discharge summary.pdf',
  /**
   * A file is read as it arrives, unlike the audio, which is read with
   * everything else at the end. A note about the file, sat under it: nothing is
   * waiting for it, so it gets no spinner and no bar.
   */
};

/**
 * The ping itself. The processing screen promises one, so the review starts with
 * it arriving rather than with them happening to come back.
 */
/**
 * What the intake leaves on the actions list. The other way into the review: the
 * flow offers it once, and this is where it waits for anyone who did not do it
 * then, alongside the claims it filled in far enough to be worth finishing.
 *
 * No author on any of them. The app set them itself off the back of what it
 * read, so there is nobody to name, and the card drops its From line when there
 * is no author to put in it.
 */
export const INTAKE_ACTIONS = [
  {
    id: 901,
    title: 'Review what you told us',
    createdBy: '',
    assignedTo: 'Assigned to you',
    who: 'VM',
    files: [],
    kind: 'user' as const,
    done: false,
  },
  {
    id: 902,
    title: 'Complete the benefits calculator',
    createdBy: '',
    assignedTo: 'Assigned to you',
    who: 'VM',
    files: [],
    kind: 'user' as const,
    done: false,
  },
  {
    id: 903,
    title: 'Complete your Attendance Allowance claim',
    createdBy: '',
    assignedTo: 'Assigned to you',
    who: 'VM',
    files: [],
    kind: 'user' as const,
    done: false,
  },
];

/**
 * The other ping. Processing runs after they have gone, so a failure to read what
 * they sent cannot surface in the conversation: they are not in it. It arrives
 * the same way the good news does, and the flow it opens is where they fix it.
 *
 * Deliberately not specific about which note. Which one it was is on the screen
 * behind it, where they can see the note it is about.
 */
export const INTAKE_PING_PROBLEM = {
  app: 'KareHero',
  title: 'We could not read part of what you sent',
  body: 'Have a look and we can sort it out.',
};

export const INTAKE_PING = {
  app: 'KareHero',
  title: "We've been through everything you told us",
  body: 'Have a look at what we filled in.',
};

/**
 * The end of the intake: everything they sent gets processed at once, and they
 * are told rather than made to wait. Nothing here asks for anything, which is
 * why it carries no action.
 */
export const INTAKE_PROCESSING = {
  title: "We're processing everything you've told us",
  description: "We'll ping you when we're done.",
};

/**
 * What the flow does when something it accepted does not work out.
 *
 * All four are rows in the conversation, not screens. The flow's whole argument
 * is that they can hand things over in any order and we read them afterwards, so
 * losing the transcript to a full page error would take away the thing they were
 * part way through. The note stays and the failure sits under it.
 *
 * The message only. What to do instead is said in the text and offered by the
 * composer below, so the alert carries no button of its own.
 *
 * A message that did not send is not in here: nothing was read and nothing
 * failed to be understood, one thing did not leave the phone, so the message
 * itself carries it. See ChatBubble's `failed`.
 *
 * `voice` and `nothing` only exist after they say they are done: reading starts
 * on the way on, so neither can surface while notes are still being added.
 */
export type FailureKind = 'upload' | 'voice' | 'nothing';

/**
 * Answers that came back instead of moving on. Not a failure row: nothing broke,
 * the answer was just short, so it reads as Keni replying rather than as an
 * error against what they sent. The flow is trying to feel human, and a red bar
 * over a half-answered name does not.
 */
/**
 * Validation, said rather than shown.
 *
 * It carries what a form's inline error carries: what we have, what is missing,
 * and why we need it. Two lines rather than one run of prose, so the thing they
 * have to do is not buried in the middle of a sentence.
 */
export const INTAKE_PUSHBACK = {
  oneName:
    'Thanks, we have Margaret.\nWe need her last name too, as it appears on official letters.',
  // Placeholder wording.
  mobileShort:
    'That number looks too short. A UK mobile number has 11 digits and starts with 07.',
  mobileLong:
    'That number looks too long. A UK mobile number has 11 digits and starts with 07.',
  mobileNotMobile:
    "That doesn't look like a mobile number. It should start with 07.",
};

/**
 * A UK mobile, typed however they like: spaces, dashes and +44 are all fine.
 * Wrong prefix first, since a landline of the right length is still wrong.
 */
export const checkMobile = (value: string) => {
  let digits = value.replace(/\D/g, '');
  if (digits.startsWith('44')) digits = `0${digits.slice(2)}`;
  if (!digits.startsWith('07')) return INTAKE_PUSHBACK.mobileNotMobile;
  if (digits.length < 11) return INTAKE_PUSHBACK.mobileShort;
  if (digits.length > 11) return INTAKE_PUSHBACK.mobileLong;
  return undefined;
};

export const INTAKE_FAILURES: Record<FailureKind, { text: string }> = {
  upload: {
    text: 'We could not read that photo. You can try another, or just type the name.',
  },
  voice: {
    text: 'We could not make out that recording. You can record it again, or type it instead.',
  },
  nothing: {
    text: 'We have not been able to pull anything useful out of this yet. A care expert can go through it with you.',
  },
};

/**
 * Where declining lands. Consent that cannot be refused is not consent, so the
 * no path has to go somewhere — but what it offers instead is undecided, so this
 * is a stop on the flows page rather than a screen.
 */
export const AI_CONSENT_DECLINED = {
  title: 'If they say no',
  note: 'What we can still do without reading what they write.',
};

// Option 1 asks the carer for their own name on a screen of its own, where
// option 2 asks it in the chat. Same fact, two shapes.
export const LEGAL_NAME = {
  title: "What's your name?",
  description: 'We need it as it appears on official letters.',
  fields: ['Title', 'First and middle names', 'Surname'],
  primary: 'Next',
};

/**
 * What the intake filled in. Progress per claim rather than one bar, because
 * the useful question is which one to do first, not how far through they are.
 *
 * The calculator leads and is drawn differently: it is the one thing to do
 * next, and it is what tells them which claim to start.
 */
export const FILLED = {
  title: "Based on what you've told us",
  description: "We've filled in this much of each one.",
  lead: {
    icon: 'calculator',
    name: 'Benefits calculator',
    description: 'Do this first. It works out which claim to start with.',
    // 14 questions, the count in the eligibility form today.
    answered: 3,
    total: 14,
  },
  items: [
    {
      icon: 'hand-holding-heart',
      name: 'Attendance Allowance',
      description: 'For the person being cared for',
      answered: 41,
      total: 64,
    },
    {
      icon: 'heart',
      name: "Carer's Allowance",
      description: 'For you',
      answered: 12,
      total: 18,
    },
    {
      icon: 'sterling-sign',
      name: 'Pension Credit',
      description: 'Tops up a low income',
      answered: 9,
      total: 22,
    },
  ],
};

/**
 * What the profile's Assess tab lists.
 *
 * One list, read by the Tell Us Once profile and by the architecture cut in the
 * sandbox, because they were showing different things under the same word and
 * the whole point of the tab is that it is where everything outstanding lives.
 *
 * Not only claims. Two of these are the app's own tools wearing a claim's shape
 * — a name, a line saying what it is for, and how far in you already are — and
 * once they sit in the same list as Attendance Allowance the difference between
 * "a benefit you might get" and "a form you are part way through" stops
 * mattering to the person looking at it. That is the argument Assess makes, and
 * it is why the tab is not called Claims any more.
 */

/**
 * The calculator at the top of the profile's Claims tab: the way into the
 * claims rather than one of them.
 *
 * Same row as the filled screen's lead, and the same name, but not the same
 * line under it. There it is telling you what order to do things in; here it is
 * the thing you tap. No progress either — this is where you find it, not where
 * you are told how far in you already are.
 */
export const CLAIMS_CALCULATOR = {
  icon: FILLED.lead.icon,
  name: FILLED.lead.name,
  description: 'Work out what you can claim',
};

/**
 * The fork after the review. Two things they could do with what we now hold, and
 * no default: one is about money and one is about care, and which matters more
 * is theirs to say. Asking beats guessing, and it is cheaper than showing them
 * both and hoping.
 *
 * Each task says how far in they already are, because the answer to "why would I
 * do this" is that most of it is done. Exploring does not: there is nothing to
 * have filled in.
 *
 * Copy is mine.
 */
export const CHOOSE_NEXT = {
  title: 'What would you like to do next?',
  options: [
    {
      id: 'calculator',
      icon: 'calculator',
      title: 'Benefits calculator',
      subtitle: 'Work out what you could claim',
      // From the claims list, so the two cannot disagree about how far in they
      // already are.
      // Same wording as the claims tab's own rows, which say "41 of 64
      // answered": the count is the same fact in both places and the chip has
      // no room for a sentence.
      filled: `${FILLED.lead.answered} of ${FILLED.lead.total} answered`,
    },
    {
      id: 'care',
      icon: 'clipboard-list',
      title: 'Care assessment',
      subtitle: `Work out what care ${RECIPIENT} needs, and start finding it`,
      // A fixture: there is no care assessment in here to count.
      filled: '8 of 26 answered',
    },
    {
      // The way out. Neither of the other two is urgent, and someone who has
      // just finished telling us everything is allowed to want a look round
      // before being handed another task.
      id: 'explore',
      icon: 'magnifying-glass',
      title: 'Just explore the app',
      subtitle: 'Have a look around. Both of these will be waiting for you',
    },
  ],
};

/**
 * The review step, per claim. Gaps lead, then the facts grouped semantically.
 *
 * A fact says where it came from: `ai` is one we read out of what they sent and
 * has not been checked, `missing` is one nothing answered. Tapping any row edits
 * it in place.
 */
/**
 * Red is not one state. Each kind clears differently, and a single Confirm would
 * make the stale case lie: tapping it would say someone checked the balance when
 * they only recognised the number.
 *
 * `missing`  nothing answered it. Supply a value.
 * `conflict` two sources disagree. Pick a side.
 * `stale`    the value is fine, its date is not. Answer a freshness question.
 * `ai`       read from what they sent, unchecked. Sunflower, not red.
 */
export type ReviewRow = {
  id: string;
  label: string;
  value: string;
  from?: 'ai' | 'missing' | 'conflict' | 'stale';
  /** For a conflict: the rival readings, each with where it came from. */
  options?: { value: string; source: string }[];
  /** For a stale value: when it was true. */
  since?: string;
  /**
   * The question only gets asked if an answer above it says so.
   *
   * Said as a chip rather than as the schema's sentence ("Only asked if the
   * answer above is Yes"), and an unanswered one is dimmed and left blank
   * rather than marked Not answered: it may never need asking, so it is not
   * work outstanding.
   */
  conditional?: boolean;
  /**
   * How the answer is given. Defaults to free text.
   *
   * A review that only knows how to edit text quietly assumes every answer is
   * typed. "Speech or hearing difficulties: No" is a pick-one and the benefits
   * in payment are a pick-several, and neither survives being turned into a
   * text field.
   */
  control?: 'text' | 'choice' | 'multi' | 'date' | 'parts';
  /** What there is to pick from, for `choice` and `multi`. */
  choices?: string[];
  /**
   * For `parts`: the fields the answer is really made of.
   *
   * A legal name is a title, first and middle names and a surname; an address
   * is three lines and a postcode. The list still shows `value`, because a row
   * reading "14 Brae Street, Edinburgh EH3 6HR" is what somebody scanning the
   * profile wants. The parts are what they get when they open it, so an edit
   * lands in the right field rather than in the middle of a sentence.
   */
  parts?: { label: string; value: string }[];
  /**
   * What puts the parts back together into `value`. A comma for an address,
   * a space for a name.
   */
  join?: string;
  /**
   * A line under the answer, for something true of the question rather than of
   * the answer. A conditional question uses it to say what opens it, so a care
   * expert filling a form in on somebody's behalf knows which ones apply.
   */
  note?: string;
  /**
   * For `date`: the answer as the input needs it, yyyy-mm-dd.
   *
   * `value` stays the line the list shows, because "19 March 1943" is how a
   * date of birth reads on a profile and "1943-03-19" is not.
   */
  iso?: string;
};

/**
 * The Attendance Allowance review. Sections and counts are the real form's:
 * 66 questions across six sections, from the AA seed. A review that showed six
 * of them would answer the wrong question, because the thing being tested is
 * whether a review of this length still feels finite.
 *
 * So each section shows what needs a person and collapses the rest to a count,
 * which is F9's answer to length. Nothing is invented as an answer: a collapsed
 * count is a count, not hidden copy.
 */
export const REVIEW = {
  // What the screen is, not what the claim is. The claim is named in the bar
  // above it — every screen inside it carries that bar — so naming it again
  // here said Attendance Allowance twice and left the screen itself unnamed.
  title: 'Review',
  description:
    "This is everything we'll send. The highlighted ones are from what you told us during onboarding.",
  /** Used instead when the claim still has gaps. */
  descriptionWithGaps:
    "This is everything we'll send. Two things still need you.",
  primary: 'Finish',
  /**
   * Only shown when the claim has gaps. Coming out of the calculator there are
   * none, so this stays off and the screen is a read of what is going.
   */
  gaps: {
    label: 'Still needed',
    rows: [
      {
        id: 'gp-address',
        label: `${RECIPIENT}'s GP address`,
        value: '',
        from: 'missing',
      },
      {
        id: 'bank',
        label: 'Her bank details',
        value: '',
        from: 'missing',
      },
    ] as ReviewRow[],
  },
  groups: [
    {
      label: 'About Margaret',
      rows: [
        {
          id: 'dob',
          label: 'Her date of birth',
          value: '19 March 1943',
          from: 'ai',
        },
        {
          id: 'address',
          label: 'Her address',
          value: '14 Brae Street, Edinburgh EH3 6HR',
          from: 'ai',
        },
        {
          id: 'ni',
          label: 'National Insurance number',
          value: 'QQ 12 34 56 C',
        },
        {
          // The calculator asks this one as a pick-several, so the review has
          // to be able to give it back the same way.
          id: 'in-payment',
          label: 'Benefits already in payment',
          value: 'Pension Credit',
          from: 'ai',
          control: 'multi',
          choices: [
            'Attendance Allowance',
            'PIP',
            'Pension Credit',
            'None of them',
          ],
        },
      ] as ReviewRow[],
      hidden: [
        { id: 'about-0', label: 'Surname or family name', value: 'Doyle' },
        {
          id: 'about-1',
          label: 'All other names in full',
          value: 'Margaret Ellen',
        },
        {
          id: 'about-2',
          label: 'Title',
          value: 'Mrs',
          control: 'choice',
          choices: ['Mrs', 'Miss', 'Ms', 'Mr', 'Dr'],
        },
        {
          id: 'about-3',
          label: 'Sex',
          value: 'Female',
          control: 'choice',
          choices: ['Female', 'Male'],
        },
        {
          id: 'about-4',
          label: 'Daytime phone number',
          value: '0131 496 0221',
        },
        {
          id: 'about-5',
          label: 'Mobile phone number if different',
          value: 'None',
        },
        {
          id: 'about-6',
          label: 'Speech or hearing difficulties',
          value: 'No',
          control: 'choice',
          choices: ['Yes', 'No'],
        },
        { id: 'about-7', label: 'Nationality', value: 'British' },
        {
          id: 'about-8',
          label: 'Does she normally live in Great Britain?',
          value: 'Yes',
          control: 'choice',
          choices: ['Yes', 'No'],
        },
        {
          id: 'about-9',
          label: 'Abroad for more than 4 weeks in the last 3 years?',
          value: 'No',
          control: 'choice',
          choices: ['Yes', 'No'],
        },
        {
          id: 'about-10',
          label: 'Swiss or EEA national living in the UK before 2021',
          value: 'Not applicable',
        },
        {
          id: 'about-11',
          label: 'Getting other benefits from an EEA state or Switzerland',
          value: 'No',
          control: 'choice',
          choices: ['Yes', 'No'],
        },
        {
          id: 'about-12',
          label: 'Working or paying insurance in an EEA state or Switzerland',
          value: 'No',
          control: 'choice',
          choices: ['Yes', 'No'],
        },
        {
          id: 'about-13',
          label: 'Claiming under the special rules',
          value: 'No',
          control: 'choice',
          choices: ['Yes', 'No'],
        },
      ] as ReviewRow[],
    },
    {
      label: 'Medication and treatments',
      rows: [
        {
          id: 'gp',
          label: `${RECIPIENT}'s GP`,
          value: 'The Grove Surgery, Hillside Road, Leeds',
          from: 'ai',
        },
        {
          id: 'conditions',
          label: 'Her illnesses',
          value: 'Vascular dementia, osteoarthritis',
          from: 'ai',
        },
      ] as ReviewRow[],
      hidden: [
        {
          id: 'meds-0',
          label: 'Does she have any illnesses or disabilities?',
          value: 'Yes',
        },
        {
          id: 'meds-1',
          label: 'Seen anyone apart from her GP in the last 12 months?',
          value: 'Yes, a memory clinic',
        },
        {
          id: 'meds-2',
          label: 'Does anyone else help her because of her illnesses?',
          value: 'Yes, her daughters',
        },
        {
          id: 'meds-3',
          label: "Her GP's address",
          value: 'Hillside Road, Leeds',
        },
        {
          id: 'meds-4',
          label: "Her GP's phone number",
          value: '0113 496 0188',
        },
        {
          id: 'meds-5',
          label: 'When she last saw them about her illnesses',
          value: '19 June',
        },
        {
          id: 'meds-6',
          label: 'Does she have any reports about her illnesses?',
          value: 'Yes, a discharge summary',
        },
        { id: 'meds-7', label: 'Has she had any tests?', value: 'Yes' },
        {
          id: 'meds-8',
          label: 'What type of accommodation she lives in',
          value: 'Terraced house, her own home',
        },
        {
          id: 'meds-9',
          label: 'Where the toilet is in her home',
          value: 'Upstairs',
        },
        {
          id: 'meds-10',
          label: 'Where she sleeps in her home',
          value: 'Upstairs bedroom',
        },
        {
          id: 'meds-11',
          label: 'What aids or adaptations she uses',
          value: 'Zimmer frame, stair rail',
        },
      ] as ReviewRow[],
    },
    {
      label: 'Help during the day',
      rows: [
        {
          id: 'washing',
          label: 'Washing, bathing or looking after herself',
          value: 'Needs help',
          from: 'ai',
        },
        {
          id: 'toilet',
          label: 'Her toilet needs',
          value: 'Needs help',
          from: 'ai',
        },
      ] as ReviewRow[],
      hidden: [
        {
          id: 'day-0',
          label: 'When she started to need this help',
          value: 'Around March 2024',
        },
        {
          id: 'day-1',
          label: 'Getting out of bed, or into bed at night',
          value: 'Needs help',
        },
        { id: 'day-2', label: 'Dressing or undressing', value: 'Needs help' },
        { id: 'day-3', label: 'Moving around indoors', value: 'Needs help' },
        { id: 'day-4', label: 'Falling or stumbling', value: 'Yes' },
        {
          id: 'day-5',
          label: 'Cutting up food, eating or drinking',
          value: 'Needs help cutting up',
        },
        {
          id: 'day-6',
          label: 'Taking her medicines',
          value: 'Needs prompting',
        },
        {
          id: 'day-7',
          label: 'Communicating with other people',
          value: 'Needs help',
        },
        {
          id: 'day-8',
          label: 'Taking part in hobbies or interests',
          value: 'Needs help',
        },
        {
          id: 'day-9',
          label: 'How many days a week she needs this help',
          value: 'Every day',
        },
        {
          id: 'day-10',
          label: 'Whether she needs someone keeping an eye on her',
          value: 'Yes',
        },
        {
          id: 'day-11',
          label: 'Waiting to hear about Constant Attendance Allowance',
          value: 'No',
        },
        {
          id: 'day-12',
          label: 'Anything else about the help she needs',
          value: 'Added from what you told us',
        },
        { id: 'day-13', label: 'On a waiting list for surgery', value: 'No' },
        {
          id: 'day-14',
          label: 'Signing the form for someone else',
          value: 'Yes, you are',
        },
      ] as ReviewRow[],
    },
    {
      label: 'Help at night',
      rows: [
        {
          id: 'night',
          label: 'Help during the night',
          value: 'Yes, two or three times a night',
          from: 'ai',
        },
        {
          id: 'night-detail',
          label: 'What happens when she wakes',
          value: 'She needs help to the toilet and settling back down',
          from: 'ai',
        },
      ] as ReviewRow[],
    },
    {
      label: 'Time in hospital',
      rows: [
        {
          id: 'hospital',
          label: 'In hospital or a care home now',
          value: 'No',
          from: 'ai',
        },
      ] as ReviewRow[],
      hidden: [
        {
          id: 'hosp-0',
          label: 'Out of hospital or a care home in the past six weeks?',
          value: 'Yes, discharged 2 July',
        },
      ] as ReviewRow[],
    },
    {
      label: 'How we pay you',
      rows: [] as ReviewRow[],
      hidden: [
        {
          id: 'pay-0',
          label: 'Name of the account holder',
          value: 'Margaret E Doyle',
        },
        {
          id: 'pay-1',
          label: 'Name of the bank or building society',
          value: 'Bank of Scotland',
        },
        { id: 'pay-2', label: 'Sort code', value: '80 22 60' },
        { id: 'pay-3', label: 'Account number', value: '••••4417' },
        {
          id: 'pay-4',
          label: 'Building society roll or reference number',
          value: 'Not applicable',
        },
        {
          id: 'pay-5',
          label: 'Other benefits we do not pay into an account',
          value: 'None',
        },
      ] as ReviewRow[],
    },
    {
      label: 'Declaration',
      rows: [] as ReviewRow[],
      hidden: [
        { id: 'decl-0', label: 'How often you see her', value: 'Every day' },
        {
          id: 'decl-1',
          label: 'Her illnesses and how she is affected by them',
          value: 'From what you told us',
        },
        {
          id: 'decl-2',
          label: 'Your job, profession or relationship to her',
          value: 'Daughter',
        },
        { id: 'decl-3', label: 'Your name', value: 'Vez Maxwell' },
        {
          id: 'decl-4',
          label: 'Your address',
          value: '14 Brae Street, Edinburgh EH3 6HR',
        },
        {
          id: 'decl-5',
          label: 'Your daytime phone number',
          value: '07700 900312',
        },
        {
          id: 'decl-6',
          label: 'Anything else you think we should know',
          value: 'Nothing added',
        },
      ] as ReviewRow[],
      note: 'Signed by you and by Margaret. We cannot touch either.',
    },
  ],
};

/**
 * Option 1's ending: everything in one read, ordered by how sure we are rather
 * than by topic. Red first, because those are the ones a person has to look at.
 *
 * Its own copy of the review content: the two options are being compared, so
 * neither ending can move the other.
 */
export const REVIEW_ALL = {
  title: "Review everything you've told us",
  primary: 'Done',
  key: [
    { tone: 'attention' as const, label: 'Definitely needs your attention' },
    {
      tone: 'ai' as const,
      label: "We're pretty sure. Here for your reference",
    },
  ],
  groups: [
    {
      label: 'Needs your attention',
      rows: [
        {
          id: 'ni',
          label: 'National Insurance number',
          value: '',
          from: 'missing',
        },
        {
          id: 'falls',
          label: 'Falls in the last year',
          value: '',
          from: 'conflict',
          options: [
            { value: 'Two', source: 'GP letter, 19 Jun' },
            { value: 'None', source: 'Discharge summary, 2 Jul' },
          ],
        },
        {
          id: 'savings',
          label: 'Savings',
          value: '£18,400',
          from: 'stale',
          since: 'a statement in May',
        },
      ] as ReviewRow[],
    },
    {
      label: 'For your reference',
      rows: [
        {
          id: 'dob',
          label: `${RECIPIENT}'s date of birth`,
          value: '19 March 1943',
          from: 'ai',
        },
        {
          id: 'address',
          label: 'Address',
          value: '14 Brae Street, Edinburgh EH3 6HR',
          from: 'ai',
        },
        {
          id: 'conditions',
          label: 'Conditions',
          value: 'Vascular dementia, osteoarthritis',
          from: 'ai',
        },
        {
          id: 'night',
          label: 'Help at night',
          value: 'Yes, up two or three times',
          from: 'ai',
        },
        { id: 'washing', label: 'Help washing', value: 'Yes', from: 'ai' },
        {
          id: 'mobility',
          label: 'Mobility',
          value: 'Assisted, walks with a frame',
          from: 'ai',
        },
      ] as ReviewRow[],
    },
  ],
};

/**
 * The care profile: persistent and browsable, with no end state. Grouped
 * semantically rather than by which claim wanted the fact, and each group says
 * how fast it goes stale, because that decides whether a confirmed fact stays
 * confirmed.
 *
 * Groups derived from the three real assessments, not invented: see the Phase 0
 * grouping.
 */
/**
 * What we heard, read back as sentences rather than as a list of fields.
 *
 * The first thing they see when the ping brings them back. A carer who has just
 * talked for two minutes wants to know we understood, and a table of twenty
 * labelled rows does not answer that: it asks them to audit us. So the summary
 * comes first and the list stays behind it, for anyone who wants to change a
 * particular thing.
 *
 * Every line here is drawn from the same facts the list holds, so the two cannot
 * say different things. Prose in the prototype, generated in the app.
 */
export const INTAKE_SUMMARY = {
  title: 'Does this look right?',
  label: 'AI summary',
  lines: [
    `You've told us you're caring for your mum, ${RECIPIENT} Doyle, around 40 hours a week, and that your sister Ruth helps.`,
    'She has vascular dementia and osteoarthritis, walks with a frame, and needs help washing, dressing, eating and through the night.',
    'You said you want help with money she might be owed, and with care at home.',
  ],
  confirm: "Yes, that's correct",
  wrong: "That's not quite right",
  /** The pencil on the card, and the button that finishes editing it. */
  edit: 'Edit summary',
  done: 'Done',
  /**
   * The same screen when the reading did not work out, and deliberately a
   * thinner one: the whole signal is how little is in the card next to the
   * three lines the happy path fills it with. An alert carries the failure,
   * because a grey line under a heading reads as a caption and this is not one.
   *
   * It still shows what it did get. A failure that only offers a retry throws
   * away everything that did work.
   */
  problemTitle: "We couldn't read some of it",
  problem:
    "Something went wrong with your answers and we couldn't get everything we needed.",
  problemLines: [
    `You've told us you're caring for your mum, ${RECIPIENT} Doyle.`,
  ],
  keep: 'Continue anyway',
  tellAgain: 'Tell us again',
};

/**
 * The conversation reopened to put something right.
 *
 * It reads back what we hold and then asks what is wrong with it. It used to
 * open on 'anything else', which is the right question at the end of an intake
 * and the wrong one here: somebody who has just tapped "That's not quite right"
 * has a correction in mind, and being met with an open invitation makes them
 * find the thing they are correcting on their own.
 *
 * The read-back is INTAKE_SUMMARY.lines, so the chat and the summary they came
 * from cannot say different things.
 */
const WHATS_WRONG: ChatQuestion = {
  id: 'whats-wrong',
  section: ABOUT_THEM,
  says: INTAKE_SUMMARY.lines,
  ask: 'What have we got wrong?',
  mode: 'say',
  // Stays open: a correction is rarely one thing, and the way on is the row
  // below rather than the first answer given.
  multi: true,
  // A new conversation. They are coming back to a finished thing, and carrying
  // the intake's transcript in would put the whole of it above this question.
  restart: true,
  asides: [
    { icon: 'chevron-right', label: "Continue when you're done", tone: 'go' },
  ],
};

/** What the chat asks when it is reopened from the summary. */
export const INTAKE_CHAT_AGAIN: ChatQuestion[] = [WHATS_WRONG, ANYTHING_ELSE];

/**
 * What Nationality offers. Short and UK-weighted on purpose: this is a claim
 * form's question, and the list a care expert picks from should be the handful
 * that come up rather than every country there is.
 */
export const NATIONALITIES = [
  'British',
  'Irish',
  'Other EU',
  'Other',
  'Prefer not to say',
];

export const CARE_PROFILE = {
  person: RECIPIENT,
  subtitle: 'Everything we hold, and where it came from.',
  primary: 'Tell us more',
  /**
   * The profile doubles as the review. Nothing is reviewed during the intake:
   * asking someone to check a pile of facts before there is anything worth
   * checking is a lot to ask of a person who has just finished telling us
   * everything. So it waits until the profile is filled, and the profile says so
   * when they arrive.
   *
   * `confirm` is the primary until they say it is right, and `primary` above is
   * what it becomes afterwards. Placeholder wording, mine.
   */
  review: {
    title: 'We filled in what we could',
    /** The review screen's own heading: whose details these are. */
    screenTitle: `About ${RECIPIENT}`,
    body: 'Change anything we got wrong.',
    /** And the line under it. */
    screenBody: `We've filled in some of ${RECIPIENT}'s profile from what you told us. Change anything we got wrong.`,
    action: 'Have a look',
    confirm: 'Looks good to me',
    /**
     * The same screen when the reading did not work out.
     *
     * Not a screen of its own: what they want to know is what we did get, and
     * that is this list. Saying so here and showing the list underneath is the
     * whole message, and it keeps the failure attached to the thing it affected.
     */
    problem:
      "Something went wrong with your answers and we couldn't get everything we needed. This is what we have.",
    tellAgain: 'Tell us again',
  },
  /**
   * Only facts with a life beyond one claim: reused across claims, changing over
   * time, or something a CAM needs. A one-off routing question stays in the
   * claim that wanted it, otherwise this becomes the care assessment with extra
   * steps.
   *
   * So the shape of her needs is here, in one line, and the per-question detail
   * behind it lives in the claim, where it is worded the way that form is judged.
   */
  groups: [
    {
      label: 'Identity',
      rows: [
        {
          id: 'name',
          label: 'Legal name',
          value: 'Margaret Ellen Doyle',
          // The same three fields the intake asks for. See LEGAL_NAME.
          control: 'parts',
          join: ' ',
          parts: [
            { label: 'Title', value: '' },
            { label: 'First and middle names', value: 'Margaret Ellen' },
            { label: 'Surname', value: 'Doyle' },
          ],
        },
        {
          id: 'dob',
          label: 'Date of birth',
          value: '19 March 1943',
          control: 'date',
          iso: '1943-03-19',
          from: 'ai',
        },
        {
          id: 'ni',
          label: 'National Insurance number',
          value: '',
          from: 'missing',
        },
        {
          id: 'nationality',
          label: 'Nationality',
          value: 'British',
          control: 'choice',
          choices: NATIONALITIES,
        },
      ] as ReviewRow[],
    },
    {
      label: 'Contact',
      rows: [
        {
          id: 'address',
          label: 'Address',
          value: '14 Brae Street, Edinburgh EH3 6HR',
          control: 'parts',
          parts: [
            { label: 'Address line 1', value: '14 Brae Street' },
            { label: 'Address line 2', value: '' },
            { label: 'Town or city', value: 'Edinburgh' },
            { label: 'Postcode', value: 'EH3 6HR' },
          ],
          from: 'stale',
          since: 'a letter in March',
        },
        { id: 'phone', label: 'Phone', value: '0131 496 0221' },
      ] as ReviewRow[],
    },
    {
      label: 'Health',
      rows: [
        {
          id: 'conditions',
          label: 'Conditions',
          value: 'Vascular dementia, osteoarthritis',
          from: 'ai',
        },
        {
          id: 'gp',
          label: 'GP',
          value: 'Dr Aisha Kerr, Brae Medical Practice',
          from: 'ai',
        },
        {
          id: 'falls',
          label: 'Falls in the last year',
          value: '',
          from: 'conflict',
          options: [
            { value: 'Two', source: 'GP letter, 19 Jun' },
            { value: 'None', source: 'Discharge summary, 2 Jul' },
          ],
        },
      ] as ReviewRow[],
    },
    {
      label: 'The care she needs',
      rows: [
        {
          id: 'needs',
          label: 'Help with',
          value: 'Washing, dressing, the toilet, eating, and at night',
          from: 'ai',
        },
        {
          id: 'mobility',
          label: 'Getting around',
          value: 'Assisted, walks with a frame',
          from: 'ai',
        },
      ] as ReviewRow[],
    },
    {
      label: 'Who cares for her',
      rows: [
        {
          id: 'carer',
          label: 'You',
          value: 'Daughter, around 40 hours a week',
        },
        { id: 'sibling', label: 'Ruth Doyle', value: 'Daughter' },
      ] as ReviewRow[],
    },
  ],
};

/**
 * The review framed as what we kept rather than what is missing.
 *
 * Going from the summary to a list headed "We filled in what we could" read as
 * the summary's detail being thrown away: the relief of seeing it all read back,
 * then a form. This leads with what their words turned into, then the gaps.
 *
 * The same rows as CARE_PROFILE, regrouped by whether they still need someone,
 * and the counts are read off them so the copy cannot say a different number.
 * Placeholder wording.
 */
const needsSomeone = (row: ReviewRow) =>
  row.from === 'missing' || row.from === 'conflict' || row.from === 'stale';
const profileRows = CARE_PROFILE.groups.flatMap((g) => g.rows);
const keptRows = profileRows.filter((row) => !needsSomeone(row));
const gapRows = profileRows.filter(needsSomeone);

export const REVIEW_KEPT = {
  title: `About ${RECIPIENT}`,
  body: `From what you told us, we added ${keptRows.length} things to her profile. ${gapRows.length} are left to fill in, now or later.`,
  groups: [
    {
      label: 'From what you told us',
      rows: keptRows,
    },
    { label: 'Still to fill in, now or later', rows: gapRows },
  ],
};

export const ASSESS_ROWS: {
  id: string;
  icon: string;
  name: string;
  description: string;
  /** Answered of total. Omitted where there is no form behind it to count. */
  answered?: number;
  total?: number;
  /**
   * What the row offers instead of a count, where there is nothing to count.
   *
   * The care profile is filled in already, so "how far through it are you" is
   * the wrong question to put on it.
   */
  action?: string;
  /** Coral rather than lime: the one to do next. */
  lead?: boolean;
}[] = [
  {
    id: 'calculator',
    icon: 'calculator',
    name: 'Benefits calculator',
    description: 'Work out what you can claim',
    lead: true,
  },
  {
    // A count, like every other form under Assess.
    //
    // It carried "Have a look" instead, on the argument that the intake fills
    // this one in, so how far through it you are is the wrong question and a
    // count would be counting work we have taken off them. It is still another
    // form under Assess, and the one row that would not say where it was up to
    // read as the odd one out rather than as the one already done.
    //
    // 9 of 10, so it reads as nearly done rather than untouched: what is left
    // is what the intake could not fill, which is the only part that needs
    // them.
    id: 'care-profile',
    icon: 'clipboard-list',
    name: 'Care assessment',
    description: CARE_PROFILE.subtitle,
    answered: 9,
    total: 10,
  },
  {
    id: 'financial-status',
    icon: 'sterling-sign',
    name: 'Financial status',
    description: 'Understand your finances',
    answered: 9,
    total: 22,
  },
  {
    id: 'attendance-allowance',
    icon: 'hand-holding-heart',
    name: 'Attendance Allowance',
    description: 'For the person being cared for',
    answered: 41,
    total: 64,
  },
  {
    // No count: there is no form behind it yet, and a zero would read as a
    // thing somebody has started and abandoned.
    id: 'power-of-attorney',
    icon: 'file-contract',
    name: 'Power of attorney',
    description: 'Get covered',
  },
];

/**
 * The profile as a tab, in the shape the master prototype uses: a segmented
 * control over one screen. Its own copy of that pattern, with the segments this
 * flow needs rather than Calls / Docs / Forms / Tools.
 *
 * Profile is the facts. Claims is what those facts fill in. Docs is what they
 * sent. Calls is who they spoke to.
 */
// The architecture cut's names, brought back here.
//
// "Claims" said what the rows were rather than what you do with them, and the
// things you can be part way through are not all claims: the calculator is a
// tool and the care assessment is a form. Assess covers all of them and leads,
// because it is the tab with something outstanding in it.
//
// "Care for" was a phrase looking for a home — Profile was taken by the
// master's record tab, Care circle by the people — and About is what the tab
// actually holds: what we know about her.
//
// The ids stay as they were. The words are what is being tested, not the
// routes, and every caller and handover mini addresses these by id.
export const PROFILE_TABS = [
  { id: 'claims', label: 'Assess' },
  { id: 'profile', label: 'About' },
  { id: 'docs', label: 'Docs' },
  { id: 'calls', label: 'Calls' },
] as const;

export type ProfileTabId = (typeof PROFILE_TABS)[number]['id'];

export const PROFILE_DOCS = [
  {
    id: 'council-tax',
    name: 'Council tax bill',
    when: '4 Aug',
    read: '3 facts',
  },
  { id: 'award', name: 'Award letter', when: '11 Apr', read: '4 facts' },
  {
    id: 'discharge',
    name: 'Discharge summary',
    when: '2 Jul',
    read: '6 facts',
  },
  { id: 'gp-letter', name: 'GP letter', when: '19 Jun', read: '2 facts' },
  {
    id: 'statement',
    name: 'Bank statement',
    when: 'May',
    read: '1 fact, going stale',
  },
];

export const PROFILE_CALLS = [
  {
    id: 'funding',
    name: 'Funding call',
    when: '10 May, 10:00',
    who: 'with Yasmin',
  },
  {
    id: 'assessment',
    name: 'Care assessment',
    when: '7 May, 10:00',
    who: 'with Yasmin',
  },
];

/** What the colours on the review screen mean, when both are on screen. */
export const REVIEW_KEY = [
  { tone: 'ai' as const, label: 'From what you told us during onboarding' },
  { tone: 'missing' as const, label: 'Still needs you' },
];

/** Placeholder. The point of the screen is what it does not ask. */
export const CALCULATOR = {
  title: 'Benefits calculator',
  note: 'Not designed yet. When it is, it only asks what we do not already have: anything collected during onboarding is skipped.',
  primary: 'Continue',
};

/** Where the self branch stops being drawn, and why. */
export const SELF_BRANCH = {
  title: 'The rest is the same, worded for them',
  note: 'Not designed yet. It asks the same things about their own situation, and there is no tell us about you section, because they are the person we are asking about.',
};

/** What the calculator hands back: the one claim to do first. */
export const ELIGIBLE = {
  title: 'Margaret could claim Attendance Allowance',
  note: 'Not designed yet. The real screen shows the amount, the confidence behind it, and what it is based on.',
  primary: 'Start the claim',
};

/**
 * The offer at the end. "Care expert", not CAM: CAM is the internal name for
 * the team and never appears to a member.
 */
export const BOOK_CALL = {
  title: 'Talk it through',
  description:
    'Book a call and your care expert will go through this claim with you before it goes anywhere.',
  primary: 'Book a call',
  secondary: 'Not now',
};

/**
 * How to use, from the calculator's composer: one line for each button, for
 * anyone unsure they can just talk or type.
 */
export const INTAKE_HOW_TO = {
  title: 'How to use',
  body: 'Answer however suits you. Type it, or use the buttons.',
  rows: [
    ['plus', "Add a photo or a letter, if that's easier than saying it."],
    [
      'microphone',
      'Tap the mic and talk. Your words show as you go, and send when you pause. Tap stop to check them first.',
    ],
  ] as const,
};
